import { Fragment, type ReactNode } from 'react';
import { text, type LocalizedText } from '../../app/i18n';
import { sanitizeRichText, type RichNode } from './rich-text';
import type { WidgetProps } from './types';

function render(nodes: readonly RichNode[]): ReactNode[] {
  return nodes.map((node, index) => {
    if (node.kind === 'text') return <Fragment key={index}>{node.text}</Fragment>;
    const children = render(node.children);
    switch (node.tag) {
      case 'br':
        return <br key={index} />;
      case 'a':
        return (
          <a key={index} href={node.href} target="_blank" rel="noopener noreferrer nofollow">
            {children}
          </a>
        );
      case 'p':
        return <p key={index}>{children}</p>;
      case 'strong':
        return <strong key={index}>{children}</strong>;
      case 'em':
        return <em key={index}>{children}</em>;
      case 'ul':
        return <ul key={index}>{children}</ul>;
      case 'ol':
        return <ol key={index}>{children}</ol>;
      case 'li':
        return <li key={index}>{children}</li>;
    }
    return null;
  });
}

function sourceText(candidate: unknown): string | undefined {
  if (typeof candidate === 'string') return candidate === '' ? undefined : candidate;
  if (typeof candidate === 'object' && candidate !== null) {
    const localized = text(candidate as LocalizedText, 'fr', '');
    return localized === '' ? undefined : localized;
  }
  return undefined;
}

/** Composant de rendu d'un texte riche assaini (aussi utilisable hors gabarit). */
export function SanitizedRichText({ source, id, labelledBy, describedBy }: { source: string; id?: string; labelledBy?: string; describedBy?: string | undefined }): ReactNode {
  return (
    <div id={id} className="ay-richtext" role="group" aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {render(sanitizeRichText(source))}
    </div>
  );
}

/**
 * Texte riche `html` en lecture seule (ADR-146 §6.1, ERP-SEC-004) d'un champ `rich_text` : le texte du
 * widget (`presentation.content`, 1.9.0) ; en repli de compatibilité, la réponse reçue, la valeur par
 * défaut, puis la description du champ. Rien n'est saisi ; avec `content`, rien n'est envoyé.
 */
export function RichTextBlock({ id, field, definition, value, describedBy, content }: WidgetProps): ReactNode {
  if (field.type !== 'rich_text') return null;
  const source = sourceText(content) ?? sourceText(value) ?? sourceText(definition?.default) ?? sourceText(definition?.description);
  if (source === undefined) return <span className="ay-richtext ay-richtext--empty">—</span>;
  return <SanitizedRichText source={source} id={id} labelledBy={`${id}-label`} describedBy={describedBy} />;
}
